import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AndroidLogo, AppleLogo, ArrowRight, CaretDown, ClockCounterClockwise, Crown, DeviceMobile, Envelope,
  GooglePlayLogo, Heart, Lightning, ListBullets, Lock, Monitor, ShieldCheck, Television, TelevisionSimple,
} from "@phosphor-icons/react";
import { ActivationSteps } from "@/components/ActivationSteps";
import { usePlans } from "@/hooks/usePlans";
import { formatPrice, planNote } from "@/lib/api";

const SUPPORT_EMAIL = "support@balkanhdplay.com";

const STORES = [
  { id: "ios", Icon: AppleLogo, top: "Preuzmite na", name: "App Store" },
  { id: "android", Icon: GooglePlayLogo, top: "Nabavite na", name: "Google Play" },
  { id: "androidtv", Icon: AndroidLogo, top: "Za", name: "Android TV" },
  { id: "appletv", Icon: TelevisionSimple, top: "Za", name: "Apple TV" },
  { id: "samsung", Icon: Television, top: "Za Samsung", name: "Smart TV" },
  { id: "lg", Icon: Monitor, top: "Za LG", name: "webOS TV" },
];

const FEATURES = [
  { Icon: Lightning, title: "Brz i stabilan", text: "Napredni plejer koji pokreće skoro svaki format bez zastajkivanja." },
  { Icon: ListBullets, title: "Xtream i M3U", text: "Dodajte svoju listu putem Xtream naloga ili M3U linka za par sekundi." },
  { Icon: Television, title: "Napravljen za TV", text: "Lako upravljanje daljinskim, pregledan meni i veliki prikaz." },
  { Icon: ClockCounterClockwise, title: "Nastavi gledanje", text: "Filmovi i serije se nastavljaju tačno tamo gde ste stali." },
  { Icon: Heart, title: "Omiljeni i grupe", text: "Sačuvajte omiljene kanale i složite ih u svoje grupe." },
  { Icon: Lock, title: "Roditeljska zaštita", text: "Zaključajte kategorije PIN kodom i zaštitite decu." },
];

const FAQ = [
  { q: "Da li aplikacija sadrži kanale ili filmove?", a: "Ne. BalkanHD Play je samo plejer. Kanale, filmove i serije dobijate od svog IPTV provajdera i unosite ih u aplikaciju (Xtream ili M3U)." },
  { q: "Da li postoji besplatan probni period?", a: "Da. Nakon instalacije aplikaciju možete besplatno koristiti tokom probnog perioda. Posle toga je aktivirate jednim od paketa ispod." },
  { q: "Gde da nađem Device Key i MAC adresu?", a: "U aplikaciji otvorite Podešavanja → „Aktiviraj / Obnovi”. Tu su prikazani Device Key, MAC adresa i QR kod za brzu aktivaciju." },
  { q: "Platio sam, a aplikacija još nije aktivna?", a: `Aktivacija traje par sekundi. Zatvorite i ponovo otvorite aplikaciju. Ako i dalje ne radi, pišite nam na ${SUPPORT_EMAIL} i pošaljite Device Key i MAC.` },
  { q: "Da li jedna licenca važi za više uređaja?", a: "Licenca važi za jedan uređaj (jedan Device Key). Za svaki dodatni TV ili telefon potrebna je posebna aktivacija." },
];

const NAV = [
  { id: "kako", label: "Kako aktivirati" },
  { id: "cene", label: "Cene" },
  { id: "preuzmi", label: "Preuzmi" },
  { id: "pitanja", label: "Pitanja" },
];

const Wrap = ({ className = "", children, ...p }) => <div className={`mx-auto w-full max-w-[1180px] px-6 ${className}`} {...p}>{children}</div>;
const Eyebrow = ({ children, center }) => <p className={`text-xs font-bold tracking-[2px] text-brand ${center ? "text-center" : ""}`}>{children}</p>;
const H2 = ({ children, center }) => <h2 className={`mt-2 text-[30px] font-bold leading-[38px] ${center ? "text-center" : ""}`}>{children}</h2>;

const Nav = () => (
  <header className="sticky top-0 z-20 border-b border-line bg-surface/95 backdrop-blur">
    <Wrap className="flex h-[68px] items-center justify-between gap-4">
      <img src="/assets/logo-full-sm.png" alt="BalkanHD Play" className="h-14 w-[140px] object-contain" data-testid="nav-logo" />
      <nav className="hidden gap-8 lg:flex">
        {NAV.map((n) => <a key={n.id} href={`#${n.id}`} data-testid={`nav-${n.id}`} className="text-sm font-medium text-txt2 transition-colors hover:text-white">{n.label}</a>)}
      </nav>
      <Link to="/activate-web" data-testid="nav-activate" className="rounded-full bg-brand px-4 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">Aktivirajte plejer</Link>
    </Wrap>
  </header>
);

const Hero = () => (
  <section className="bg-cover bg-center" style={{ backgroundImage: "url(/assets/hero-bg.jpg)" }}>
    <div className="bg-[rgba(7,7,8,0.78)] py-[88px]">
      <Wrap className="flex flex-col gap-4">
        <span className="fade-up inline-flex items-center gap-1.5 self-start rounded-full bg-brandTert px-3 py-1.5 text-xs font-semibold text-onBrandTert">
          <ShieldCheck size={14} weight="fill" className="text-brand" /> Besplatan probni period
        </span>
        <h1 className="fade-up text-[40px] font-bold leading-[46px] tracking-tight lg:text-[64px] lg:leading-[70px]" style={{ animationDelay: "80ms" }} data-testid="landing-hero-title">
          Vaša IPTV lista.<br /><span className="text-brand">Najlepši plejer.</span>
        </h1>
        <p className="fade-up max-w-[620px] text-base leading-[26px] text-txt2" style={{ animationDelay: "160ms" }}>
          BalkanHD Play je premium plejer za TV, telefon i tablet. Uživo kanali, filmovi i serije iz vaše liste — sve na jednom mestu, brzo i pregledno.
        </p>
        <div className="fade-up mt-2 flex flex-wrap gap-3" style={{ animationDelay: "240ms" }}>
          <Link to="/activate-web" data-testid="hero-activate" className="btn-primary">Aktivirajte plejer <ArrowRight size={18} weight="bold" /></Link>
          <a href="#kako" data-testid="hero-how" className="btn-ghost">Kako funkcioniše?</a>
        </div>
        <div className="mt-4 flex flex-wrap gap-6">
          {[{ Icon: Television, t: "Smart TV" }, { Icon: AndroidLogo, t: "Android TV" }, { Icon: DeviceMobile, t: "iPhone i Android" }].map(({ Icon, t }) => (
            <span key={t} className="flex items-center gap-1.5 text-xs font-medium text-txt2"><Icon size={18} />{t}</span>
          ))}
        </div>
      </Wrap>
    </div>
  </section>
);

const Features = () => (
  <Wrap className="py-[72px]">
    <Eyebrow>ZAŠTO BALKANHD PLAY</Eyebrow>
    <H2>Sve što vam treba za gledanje</H2>
    <div className="mt-6 grid gap-4 lg:grid-cols-3">
      {FEATURES.map(({ Icon, title, text }, i) => (
        <div key={title} data-testid={`feature-${i}`} className="flex flex-col gap-2 rounded-[20px] border border-line bg-surface2 p-6 transition-colors hover:border-lineStrong">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-brandTert"><Icon size={22} weight="fill" className="text-brand" /></div>
          <h3 className="mt-1 text-base font-bold">{title}</h3>
          <p className="text-sm leading-[21px] text-txt2">{text}</p>
        </div>
      ))}
    </div>
  </Wrap>
);

const HowTo = () => (
  <section id="kako" className="scroll-mt-16 border-y border-line bg-surface2">
    <Wrap className="flex flex-col gap-6 py-[72px] lg:flex-row lg:items-start">
      <div className="lg:flex-1 lg:pr-8">
        <Eyebrow>AKTIVIRAJTE PLEJER</Eyebrow>
        <H2>Aktivacija u 5 jednostavnih koraka</H2>
        <p className="mt-3 text-base leading-[26px] text-txt2">Nije potrebno nikakvo tehničko znanje. Sve traje manje od 2 minuta, a potreban vam je samo telefon i platna kartica.</p>
        <div className="mt-6 flex flex-col gap-1 rounded-xl bg-brandTert p-4">
          <p className="text-sm font-bold text-onBrandTert">Savet</p>
          <p className="text-sm leading-[21px] text-txt2">Najlakše je da telefonom skenirate QR kod koji vidite na TV-u — tada ne morate ništa da kucate.</p>
        </div>
        <Link to="/activate-web" data-testid="how-activate" className="btn-primary mt-6">Aktivirajte sada <ArrowRight size={18} weight="bold" /></Link>
      </div>
      <div className="lg:flex-[1.2]"><ActivationSteps /></div>
    </Wrap>
  </section>
);

const Pricing = () => {
  const { plans, loading, error } = usePlans();
  return (
    <Wrap id="cene" className="scroll-mt-16 py-[72px]" data-testid="landing-pricing-section">
      <Eyebrow center>CENE</Eyebrow>
      <H2 center>Jednostavno i bez skrivenih troškova</H2>
      <p className="mt-3 text-center text-base text-txt2">Bez mesečne pretplate. Platite jednom i gledajte.</p>
      {loading && <div className="mx-auto mt-6 h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" data-testid="plans-loading" />}
      {error && <p className="mt-3 text-center text-txt2" data-testid="plans-error">Cene trenutno nisu dostupne.</p>}
      <div className="mx-auto mt-8 grid max-w-[820px] gap-4 md:grid-cols-2">
        {plans.map((p) => {
          const best = p.days === null;
          return (
            <div key={p.id} data-testid={`landing-plan-${p.id}`} className={`relative flex flex-col gap-2 rounded-[20px] bg-surface2 p-6 ${best ? "border-2 border-brand" : "border border-line"}`}>
              {best && <span className="absolute right-4 top-4 rounded-full bg-brand px-3 py-1 text-[11px] font-bold tracking-wider text-white">NAJBOLJA VREDNOST</span>}
              <Crown size={28} weight="fill" className={best ? "text-brand" : "text-txt2"} />
              <h3 className="mt-2 text-xl font-semibold">{p.name}</h3>
              <p className="text-[44px] font-bold leading-tight" data-testid={`landing-plan-price-${p.id}`}>{formatPrice(p)}</p>
              <p className="mb-2 text-sm text-muted2">{planNote(p)}</p>
              {["Svi kanali, filmovi i serije iz vaše liste", "Sve funkcije plejera", "Podrška putem e-pošte"].map((f) => (
                <span key={f} className="flex items-center gap-2 text-sm text-txt2"><ShieldCheck size={16} weight="fill" className="text-ok" />{f}</span>
              ))}
              <Link to="/activate-web" data-testid={`landing-buy-${p.id}`} className={`${best ? "btn-primary" : "btn-ghost"} mt-4`}>Izaberi paket</Link>
            </div>
          );
        })}
      </div>
    </Wrap>
  );
};

const Stores = () => {
  const [soon, setSoon] = useState(null);
  return (
    <section id="preuzmi" className="scroll-mt-16 border-y border-line bg-surface2">
      <Wrap className="py-[72px]">
        <Eyebrow center>PREUZMI</Eyebrow>
        <H2 center>Dostupno na svim vašim ekranima</H2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {STORES.map(({ id, Icon, top, name }) => (
            <button key={id} onClick={() => setSoon(id)} data-testid={`store-${id}`} className="flex min-w-[230px] items-center gap-3 rounded-xl border border-lineStrong bg-surface px-4 py-3 text-left transition-colors hover:border-muted2">
              <Icon size={30} weight="fill" />
              <div>
                <p className="text-[11px] text-muted2">{soon === id ? "Uskoro dostupno" : top}</p>
                <p className="text-base font-bold">{name}</p>
              </div>
              <span className="ml-auto rounded-md bg-surface3 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-muted2">USKORO</span>
            </button>
          ))}
        </div>
      </Wrap>
    </section>
  );
};

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <Wrap id="pitanja" className="max-w-[820px] scroll-mt-16 py-[72px]">
      <Eyebrow center>PITANJA</Eyebrow>
      <H2 center>Često postavljana pitanja</H2>
      <div className="mt-6 flex flex-col gap-2">
        {FAQ.map((f, i) => (
          <button key={f.q} onClick={() => setOpen(open === i ? null : i)} data-testid={`faq-${i}`} className="rounded-xl border border-line bg-surface2 p-4 text-left">
            <div className="flex items-center gap-3">
              <span className="flex-1 text-base font-semibold">{f.q}</span>
              <CaretDown size={18} weight="bold" className={`text-muted2 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </div>
            {open === i && <p className="mt-3 text-sm leading-[22px] text-txt2">{f.a}</p>}
          </button>
        ))}
      </div>
    </Wrap>
  );
};

const Footer = () => (
  <footer className="border-t border-line py-8">
    <Wrap className="flex flex-col gap-4">
      <div className="flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center">
        <img src="/assets/logo-full-sm.png" alt="BalkanHD Play" className="h-12 w-[130px] object-contain" />
        <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="footer-email" className="flex items-center gap-2 text-sm font-semibold"><Envelope size={18} weight="fill" className="text-brand" />{SUPPORT_EMAIL}</a>
      </div>
      <p className="text-xs leading-[18px] text-muted2">BalkanHD Play je neutralni medija plejer za IPTV/Xtream liste. Aplikacija ne sadrži i ne obezbeđuje kanale, filmove ni serije — sadržaj unosi korisnik od svog provajdera.</p>
      <p className="text-xs text-muted2">© {new Date().getFullYear()} BalkanHD Play. Sva prava zadržana. • <a href="/privacy" className="underline hover:text-white" data-testid="footer-privacy">Politika privatnosti</a></p>
    </Wrap>
  </footer>
);

export default function Landing() {
  return (
    <div className="min-h-screen scroll-smooth bg-surface" data-testid="landing-page">
      <Nav />
      <Hero />
      <Features />
      <HowTo />
      <Pricing />
      <Stores />
      <Faq />
      <Footer />
    </div>
  );
}
